// ────────────────────────────────────────────────
// src/components/Mall.js – Koala Quest Mall ♻ qty-aware version
// ────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  increment,
  onSnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import "./Mall.css";
import { createEgg } from "../Pet/Pet";

/* ───────── Mall card ───────── */
const ItemCard = ({ title, img, price, onBuy, disabled, maxed }) => (
  <div className="mall-card">
    <img src={img} alt={title} className="mall-card-img" />

    {maxed && <span className="mall-max-badge">MAX</span>}

    <div className="mall-card-body">
      <h4 className="mall-card-title">{title}</h4>
      <p className="mall-card-price">
        <span className="mall-coin">🪙</span> {price}
      </p>
      <button className="mall-card-btn" onClick={onBuy} disabled={disabled}>
        {disabled ? "Owned" : "Buy"}
      </button>
    </div>
  </div>
);

/* ───────── stock definitions ───────── */
const eggs = [
  { sku: "egg_water",  name: "Azure Frost Egg", file: "BlueIceEgg.png",   element: "water",  price: 5 },
  { sku: "egg_earth",  name: "Duskhorn Shell",  file: "BrownScaleEgg.png",element: "earth",  price: 5 },
  { sku: "egg_light",  name: "The Gold Egg",    file: "GoldGlimmerEgg.png",element: "light", price: 10 },
  { sku: "egg_wind",   name: "Vert Spike Egg",  file: "GreenSpikeEgg.png",element: "wind",   price: 5 },
  { sku: "egg_nature", name: "Sunfur Orb",      file: "OrangeFurryEgg.png",element:"nature", price: 5 },
  { sku: "egg_fire",   name: "Inferno Core Egg",file: "RedFireEgg.png",   element: "fire",   price: 5 },
];

const supplies = [
  { sku: "spring_water",  name: "Spring Water",  img: "/images/Pet/shop/SpringWater.png",  price: 1 },
  { sku: "berry_snack",   name: "Berry Snack",   img: "/images/Pet/shop/BerrySnack.png",   price: 1 },
];

const powerups = [
  { sku: "evolving_rock",   name: "Evolving Rock",   img: "/images/Pet/shop/evolving_rock.png",   price: 10 },
  { sku: "element_of_life", name: "Element of Life", img: "/images/Pet/shop/element_of_life.png", price: 10 },
];

/* ───────────────── Mall component ───────────────── */
export default function Mall() {
  const user = getAuth().currentUser;
  const [coins,   setCoins]   = useState(0);
  const [bag,     setBag]     = useState([]);
  const [busy,    setBusy]    = useState(false);

  /* live coins + backpack */
  useEffect(() => {
    if (!user) return;

    const unsubUser = onSnapshot(doc(db, "users", user.uid), s =>
      setCoins(s.data()?.coins ?? 0)
    );
    const unsubBag  = onSnapshot(collection(db, "users", user.uid, "backpack"), s =>
      setBag(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubUser(); unsubBag(); };
  }, [user?.uid]);

  /* helpers */
  const qtyMap = useMemo(
    () => bag.reduce((acc, it) => ({ ...acc, [it.sku]: it.qty ?? 1 }), {}),
    [bag]
  );
  const findDoc = sku => bag.find(b => b.sku === sku);

  const enough = price =>
    coins >= price || (alert("Not enough coins!"), false);

  const spend = price =>
    updateDoc(doc(db, "users", user.uid), { coins: increment(-price) });

  /* ── egg purchase: equip immediately ── */
  const buyEgg = async egg => {
    if (!user || busy || !enough(egg.price)) return;
    setBusy(true);
    try {
      await spend(egg.price);

      const eggObj = createEgg(egg.element);
      eggObj.photoUrl = `/images/Pet/egg/${egg.file}`;
      eggObj.sku      = egg.sku;

      await setDoc(doc(db, "users", user.uid, "pets", "current"), eggObj, { merge:false });
      alert(`${egg.name} purchased! Check your dashboard 🥚`);
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── consumable purchase: stack up to 10 ── */
  const buyItem = async item => {
    if (!user || busy) return;

    const currentQty = qtyMap[item.sku] ?? 0;
    if (currentQty >= 10) { alert("Max quantity reached (10)"); return; }
    if (!enough(item.price)) return;

    setBusy(true);
    try {
      await spend(item.price);

      const col  = collection(db, "users", user.uid, "backpack");
      const docRef = findDoc(item.sku);

      if (docRef) {
        await updateDoc(doc(col, docRef.id), { qty: docRef.qty + 1 });
      } else {
        await addDoc(col, {
          sku: item.sku,
          name: item.name,
          photoUrl: item.img,
          qty: 1,
        });
      }
      alert(`${item.name} purchased ✓`);
    } catch (e) {
      console.error(e); alert(e.message);
    } finally { setBusy(false); }
  };

  /* ── render ── */
  return (
    <div className="mall-page">
      <h2 className="mall-title">Koala Quest Mall</h2>
      <p className="mall-balance">
        Balance: <span className="mall-coin">🪙</span> {coins}
      </p>

      <Section title="Mystic Eggs">
        {eggs.map(e => (
          <ItemCard
            key={e.sku}
            title={e.name}
            img={`/images/Pet/egg/${e.file}`}
            price={e.price}
            onBuy={() => buyEgg(e)}
            disabled={busy}
          />
        ))}
      </Section>

      <Section title="Food & Water">
        {supplies.map(s => (
          <ItemCard
            key={s.sku}
            title={s.name}
            img={s.img}
            price={s.price}
            onBuy={() => buyItem(s)}
            disabled={busy || qtyMap[s.sku] >= 10}
            maxed={qtyMap[s.sku] >= 10}
          />
        ))}
      </Section>

      <Section title="Power-ups">
        {powerups.map(p => (
          <ItemCard
            key={p.sku}
            title={p.name}
            img={p.img}
            price={p.price}
            onBuy={() => buyItem(p)}
            disabled={busy || qtyMap[p.sku] >= 10}
            maxed={qtyMap[p.sku] >= 10}
          />
        ))}
      </Section>
    </div>
  );
}

/* section wrapper */
const Section = ({ title, children }) => (
  <section>
    <h3 className="mall-section-title">{title}</h3>
    <div className="mall-row">{children}</div>
  </section>
);
